import { ColumnsType } from "antd/es/table";
import { useCallback, useMemo } from "react";
import { generatePath, Link } from "react-router-dom";
import { Space } from "antd";
import { CalendarOutlined, EyeOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import { EventTemplate } from "@ticketingplatform/api/dist/recurringEvent/fragments/__generated__/eventTemplate";
import { useTableQueryParams } from "hooks/useTableQueryParams";
import { pageToSkip, renderTableDateZonedColumn } from "utils/tableUtils";
import {
  getColumnTextFilterProps,
  getTextFilterInput,
} from "components/data/TableTextFilter/utils";
import { useVenueContext } from "contexts/VenueContext";
import { ROUTE_PATHS } from "const/paths";
import {
  EventStatus,
  EventType,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import { GetEventTemplatesVariables } from "@ticketingplatform/api/dist/recurringEvent/__generated__/queries";
import {
  getColumnEnumFilterProps,
  getEnumFilterValue,
} from "components/data/TableEnumFilter/utils";
import { useEventTemplateListQuery } from "@ticketingplatform/api/dist/recurringEvent/hooks";

import { UseRecuringEventListReturnType } from "./types";

export const useRecuringEventList = (): UseRecuringEventListReturnType => {
  const { venue } = useVenueContext();
  const [{ page, pageSize, orderBy, filters }, setTableQueryParams] =
    useTableQueryParams<EventTemplate>();

  const variables = useMemo<GetEventTemplatesVariables>(
    () => ({
      recurringSettingsInclude: false,
      venueInclude: false,
      imageInclude: false,
      seatMapInclude: false,
      ticketPoolsInclude: false,
      presalePasswordsInclude: false,
      signUpOptionsInclude: false,
      discountCodesInclude: false,
      take: pageSize,
      skip: pageToSkip(page, pageSize),
      filters: {
        ...(venue ? { venueId: { equals: venue.id } } : {}),
        ...getTextFilterInput(filters?.name, "name"),
        ...getEnumFilterValue(filters?.type, "type", EventType),
        ...getEnumFilterValue(filters?.status, "status", EventStatus),
      },
      orderBy,
    }),
    [
      filters?.name,
      filters?.status,
      filters?.type,
      orderBy,
      page,
      pageSize,
      venue,
    ]
  );

  const { data, loading } = useEventTemplateListQuery({
    variables,
  });

  const onChange = useCallback<UseRecuringEventListReturnType["onChange"]>(
    ({ current, pageSize: newPageSize }, newFilters, sorter) => {
      setTableQueryParams({
        page: current,
        pageSize: newPageSize,
        sorter,
        filters: newFilters,
      });
    },
    [setTableQueryParams]
  );

  const { nodes: dataSource, count: total } = data?.eventTemplates || {};

  return useMemo(
    () => ({
      dataSource: dataSource || [],
      loading,
      onChange,
      pagination: {
        pageSize,
        current: page,
        total,
      },
    }),
    [dataSource, loading, onChange, page, pageSize, total]
  );
};
export const useRecuringEventColumns = () => {
  const intl = useIntl();
  const [{ sortOrder, filters }] = useTableQueryParams<EventTemplate>();

  const columns: ColumnsType<EventTemplate> = useMemo(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: "Name",
        }),
        dataIndex: "name",
        key: "name",
        sorter: {
          multiple: 1,
        },
        sortOrder: sortOrder.name,
        ...getColumnTextFilterProps("name", filters?.name),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Date",
        }),
        key: "date",
        dataIndex: "date",
        sorter: {
          multiple: 2,
        },
        sortOrder: sortOrder.date,
        render: renderTableDateZonedColumn("date", "timezone"),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Type",
        }),
        key: "type",
        dataIndex: "type",
        sorter: {
          multiple: 3,
        },
        sortOrder: sortOrder.type,
        ...getColumnEnumFilterProps("type", filters?.type, false, EventType),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Status",
        }),
        key: "status",
        dataIndex: "status",
        sorter: {
          multiple: 4,
        },
        sortOrder: sortOrder.status,
        ...getColumnEnumFilterProps(
          "status",
          filters?.status,
          false,
          EventStatus
        ),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Actions",
        }),
        key: "action",
        render: (_, { id }) => (
          <Space size="middle">
            <Link
              to={generatePath(ROUTE_PATHS.RecuringEventDetails, {
                id: id.toString(),
              })}
            >
              <EyeOutlined />
            </Link>
            <Link to={`${ROUTE_PATHS.Dashboard}?eventId=${id}`}>
              <CalendarOutlined />
            </Link>
          </Space>
        ),
      },
    ],
    [
      intl,
      sortOrder.name,
      sortOrder.date,
      sortOrder.type,
      sortOrder.status,
      filters?.name,
      filters?.type,
      filters?.status,
    ]
  );

  return { columns };
};
